import React, { ReactNode } from "react";
import {
  Box,
  Slider,
  SliderProps,
  IconButton,
  Typography,
  useMediaQuery,
  Theme,
  alpha,
  TextField,
} from "@mui/material";
import styled from "@emotion/styled";
import {
  FieldValues,
  useController,
  UseControllerProps,
} from "react-hook-form";
import PlusIcon from "@icons/myicons/plus-circled.svg?react";
import MinusIcon from "@icons/myicons/minus-circled.svg?react";
import sliderDots from "@icons/slider-dots.svg";
import { toGermanNumberFormat } from "@helpers/numberFormat";
import { Tooltip } from "../../Tooltip";

const Label = styled.label`
  display: inline-block;
  margin-bottom: ${({ theme }) => theme.spacing(1)};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.secondary};
`;

const Thumb = styled(Box, {
  shouldForwardProp: (prop) => prop !== "ownerState",
})(({ theme }) => ({
  width: "32px",
  height: "32px",
  borderRadius: "50%",
  border: "2px solid rgba(38, 50, 64, 0.12)",
  boxShadow: theme.shadows[2],
  position: "absolute",
  transform: "translate(-55%, -47%)",
  boxSizing: "border-box",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: `white url(${sliderDots}) no-repeat center center`,
  backgroundClip: "padding-box",
  transition: "border 0.2s ease-in-out, background 0.2s ease-in-out",

  '&[style="left: 100%;"]': {
    transform: "translate(-35%, -47%)",
  },

  "&:hover": {
    border: `2px solid ${alpha(theme.palette.primary.main, 0.4)}`,
  },

  "&.Mui-active": {
    border: `2px solid ${alpha(theme.palette.primary.main, 0.8)}`,
    backgroundClip: "padding-box",
  },
  [theme.breakpoints.down("sm")]: {
    width: "28px",
    height: "28px",
    transform: "translate(-60%, -47%)",

    '&[style="left: 100%;"]': {
      transform: "translate(-30%, -47%)",
    },
  },
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  "& .MuiSlider-rail": {
    width: "calc(100% + 30px)",
    marginLeft: "-15px",
  },
  "& .MuiSlider-track": {
    marginLeft: "-15px",

    "&[style='left: 0%; width: 100%;']": {
      width: "calc(100% + 32px) !important",
    },
  },

  [theme.breakpoints.down("sm")]: {
    "& .MuiSlider-rail": {
      marginLeft: "-14px",
    },
    "& .MuiSlider-track": {
      marginLeft: "-14px",
      "&[style='left: 0%; width: 100%;']": {
        width: "calc(100% + 28px) !important",
      },
    },
  },
}));

const ValueLabel = (props: any) => {
  return (
    <Tooltip open={props.open} text={props.value} isSmall>
      {props.children}
    </Tooltip>
  );
};

const MinMaxLabels = ({
  min,
  max,
  numericValueFormatter,
}: {
  min: number;
  max: number;
  numericValueFormatter: (value: number) => string;
}) => {
  const isLessSm = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: -3,
      }}
    >
      <Typography
        variant={isLessSm ? "caption" : "body2"}
        color="textSecondary"
        fontWeight={700}
      >
        {numericValueFormatter(min)}
      </Typography>
      <Typography
        variant={isLessSm ? "caption" : "body2"}
        color="textSecondary"
        fontWeight={700}
      >
        {`> ${numericValueFormatter(max)}`}
      </Typography>
    </Box>
  );
};

const formatOptionsToMarks = (options: Record<string, string | number>) => {
  const optionKeys = Object.keys(options);

  return optionKeys.map((key, i) => ({
    value: (100 / (optionKeys.length - 1)) * i,
    label: i === 0 || i === optionKeys.length - 1 ? options[key] : "",
  }));
};

const formatValueToNumber = (
  options: Record<string, string | number>,
  value: string
) => {
  const valueIndex = Object.keys(options).indexOf(value);
  if (valueIndex === -1) return 0;

  return (100 / (Object.keys(options).length - 1)) * valueIndex;
};

const formatNumberToValue = (
  options: Record<string, string | number>,
  value: number
) => {
  const index = Math.round(value / (100 / (Object.keys(options).length - 1)));
  return Object.keys(options)[index];
};

type Props<T extends FieldValues> = Omit<
  SliderProps,
  "name" | "defaultValue"
> & {
  name: string;
  label?: ReactNode;
  options?: Record<string, string | number>;
  defaultValue?: string;
  testId?: string;
  min?: number;
  max?: number;
  withInput?: boolean;
  labelsPosition?: "top" | "bottom";
  numericValueFormatter?: (value: number) => string;
} & UseControllerProps<T>;

export const RHFSlider = <T extends FieldValues>({
  name,
  control,
  defaultValue,
  testId,
  label,
  options,
  min,
  max,
  withInput,
  labelsPosition = "top",
  numericValueFormatter = toGermanNumberFormat,
  ...props
}: Props<T>) => {
  let optionKeys: string[], marks;
  let step: number | undefined = undefined;
  if (options) {
    optionKeys = Object.keys(options);
    marks = formatOptionsToMarks(options);
    step = 100 / (optionKeys.length - 1);
  }

  const { field } = useController({
    control,
    name,
    defaultValue,
  });

  const increaseValue = () => {
    if (!options || !step) return;
    const currentNumber = formatValueToNumber(options, field.value as string);
    if (currentNumber !== 100) {
      field.onChange(formatNumberToValue(options, currentNumber + step));
    }
  };

  const decreaseValue = () => {
    if (!options || !step) return;
    const currentNumber = formatValueToNumber(options, field.value as string);
    if (currentNumber !== 0) {
      field.onChange(formatNumberToValue(options, currentNumber - step));
    }
  };

  const handleSliderChange = (event: Event, value: number | number[]) => {
    if (!options) return field.onChange(value);
    return field.onChange(formatNumberToValue(options, value as number));
  };

  return (
    <Box data-testid={testId}>
      {label && (
        <Box mb={3}>
          <Label htmlFor={name}>{label}</Label>
        </Box>
      )}
      {min !== undefined && max !== undefined && labelsPosition === "top" && (
        <MinMaxLabels
          min={min}
          max={max}
          numericValueFormatter={numericValueFormatter}
        />
      )}
      <Box display="flex" alignItems="flex-start" gap={3} px={4}>
        {options && (
          <IconButton
            sx={{ width: "36px", height: "36px" }}
            onClick={decreaseValue}
          >
            <MinusIcon />
          </IconButton>
        )}
        <StyledSlider
          step={step}
          onChange={handleSliderChange}
          marks={marks}
          value={
            options
              ? formatValueToNumber(options, field.value as string)
              : Number(field.value)
          }
          min={min}
          max={max}
          components={{
            Thumb,
            ValueLabel,
          }}
          valueLabelDisplay="auto"
          valueLabelFormat={(value: number) => {
            if (!options || !optionKeys) return numericValueFormatter(value);

            const index = Math.round(value / (100 / (marks.length - 1)));
            return options[optionKeys[index]];
          }}
          {...props}
        />
        {options && (
          <IconButton
            sx={{ width: "36px", height: "36px" }}
            onClick={increaseValue}
          >
            <PlusIcon />
          </IconButton>
        )}
      </Box>
      {min !== undefined &&
        max !== undefined &&
        labelsPosition === "bottom" && (
          <MinMaxLabels
            min={min}
            max={max}
            numericValueFormatter={numericValueFormatter}
          />
        )}
      {withInput && (min || max) && (
        <Box
          display="flex"
          justifyContent="center"
          width="100%"
          mt={labelsPosition === "bottom" ? 4 : 1}
        >
          <TextField
            {...field}
            value={numericValueFormatter(field.value) || 0}
            type="text"
            variant="outlined"
            onChange={(e) => {
              const value = e.target.value.replace(/[^0-9]/gi, "");
              if (Number.isNaN(Number(value))) return;
              field.onChange(Number(value));
            }}
            slotProps={{
              htmlInput: {
                "data-testid": testId,
                pattern: "[0-9]*",
                sx: {
                  width: "100px",
                  "&::placeholder": {
                    color: "text.secondary",
                    opacity: 1,
                  },
                },
              },
            }}
          />
        </Box>
      )}
    </Box>
  );
};
